import { useCallback, useMemo, useState } from "react";
import { toPercent } from "@helpers";
import { useChartColors } from "@hooks";
import type { Breakdown, ClassificationBreakdownData } from "@components";

type BreakdownWithColors = Breakdown & {
  fillColor: string;
};

const buildWithColors = (breakdowns: Breakdown[], colors: string[]) =>
  breakdowns.map((breakdown, index) => ({
    ...breakdown,
    fillColor: colors[index % colors.length],
  }));

const convertToPieData = (
  breakdowns: BreakdownWithColors[]
): ClassificationBreakdownData[] => {
  const sum = breakdowns.reduce((acc, { value }) => acc + value, 0);
  if (sum <= 0) {
    return [];
  }
  return breakdowns.map((b) => ({
    uid: b.uid,
    name: b.name,
    value: Math.round(b.value * 100) / 100,
    id: b.id,
    percent: Math.round((b.value / sum) * 10000) / 100,
    hasChildren: b.hasChildren,
    fillColor: b.fillColor,
    countryCode: b.countryCode,
    regionType: b.regionType,
    isin: b.isin,
    tickerSymbol: b.tickerSymbol,
    quoteProvider: b.quoteProvider,
    investmentType: b.investmentType,
  }));
};

export const useClassificationDrillDown = (
  breakdowns: Breakdown[],
  legendSectionTitle: string
) => {
  const [drillParentId, setDrillParentId] = useState<number | undefined>(
    undefined
  );
  const [hoveredItem, setHoveredItem] =
    useState<ClassificationBreakdownData | null>(null);

  const colors = useChartColors("classificationChart");

  const rootRows = useMemo(
    () =>
      breakdowns
        .filter(({ parentId }) => parentId === undefined)
        .sort((a, b) => b.value - a.value),
    [breakdowns]
  );

  const rootTotalValue = useMemo(
    () => rootRows.reduce((acc, { value }) => acc + value, 0),
    [rootRows]
  );

  const currentRows = useMemo(() => {
    const rows =
      drillParentId === undefined
        ? rootRows
        : breakdowns
            .filter(({ parentId }) => parentId === drillParentId)
            .sort((a, b) => b.value - a.value);
    return buildWithColors(rows, colors);
  }, [breakdowns, drillParentId, rootRows, colors]);

  const currentTotalValue = useMemo(
    () => currentRows.reduce((acc, { value }) => acc + value, 0),
    [currentRows]
  );

  const donutData: ClassificationBreakdownData[] = useMemo(
    () => convertToPieData(currentRows),
    [currentRows]
  );

  const mainBreakdown = useMemo(
    () =>
      drillParentId === undefined
        ? undefined
        : breakdowns.find((b) => b.id === drillParentId),
    [breakdowns, drillParentId]
  );

  const mainParentBreakdown = useMemo(() => {
    if (!mainBreakdown || mainBreakdown.parentId === undefined) {
      return undefined;
    }
    return breakdowns.find((b) => b.id === mainBreakdown.parentId);
  }, [breakdowns, mainBreakdown]);

  const breadcrumbs = useMemo(() => {
    if (drillParentId === undefined) {
      return [];
    }
    const items: { name: string; onClick: () => void }[] = [
      {
        name: legendSectionTitle,
        onClick: () => {
          setDrillParentId(undefined);
        },
      },
    ];

    if (mainParentBreakdown && mainParentBreakdown.id !== undefined) {
      items.push({
        name: mainParentBreakdown.name ?? "",
        onClick: () => {
          setDrillParentId(mainParentBreakdown.id);
        },
      });
    }
    return items;
  }, [drillParentId, legendSectionTitle, mainParentBreakdown]);

  const legendChartTitle = mainBreakdown?.name ?? legendSectionTitle;

  const portfolioShareLabel =
    drillParentId !== undefined && rootTotalValue > 0
      ? toPercent(currentTotalValue / rootTotalValue)
      : undefined;

  const legendItems = useMemo(
    () => [...donutData].sort((a, b) => b.value - a.value),
    [donutData]
  );

  const handleDrill = useCallback((item: ClassificationBreakdownData) => {
    if (item.hasChildren && item.id !== undefined) {
      setDrillParentId(item.id);
    }
  }, []);

  const handleHover = useCallback(
    (item: ClassificationBreakdownData | null) => {
      setHoveredItem(item);
    },
    []
  );

  return {
    donutData,
    hoveredItem,
    currentTotalValue,
    mainBreakdown,
    rootTotalValue,
    breadcrumbs,
    legendChartTitle,
    portfolioShareLabel,
    legendItems,
    handleSliceClick: handleDrill,
    handleLegendClick: handleDrill,
    handleHover,
  };
};
